// @ts-nocheck
import React, { Suspense, lazy, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Dialog, Transition } from '@headlessui/react';
import { setModalOpen, setModalState, closeModalWithResult, modalViewsConst } from "../../store/modalSlice";
import { Skeleton, SkeletonText } from "../Skeleton";
import { RootState } from "../../store";
import ReactGA from "react-ga4";

// Import non-lazy components that are needed immediately
import NewAssetGroupModal from "./NewAssetGroupModal";
import NewAssetModal from "./NewAssetModal";
import { AddGroupFirst } from "./AddGroupFirst";
import DeleteApprove from "./DeleteApprove";
import PerformanceCurveForm from "./PerformanceCurveForm";
import AssetPCurveEdit from "./AssetPCurveEdit";
import Notification from "./Notification";
import PCurveDisplay from "./PCurveDisplay";
import SolarModelEnergyDisplay from "./SolarModelEnergyDisplay";
import PlatformConsentModal from "./PlatformConsentModal";
import AssetImageUploadModal from "../imageUploader/imageUploadModals/AssetImageUploadModal";
import AssetGroupImageUploadModal from "../imageUploader/imageUploadModals/AssetGroupImageUploadModal";
import DynamicProcessedModelSchemaModal from "./DynamicProcessedModelSchemaModal";
import CustomForecastModal from "./CustomForecastModal";
import PersonaDetailsModal from "./PersonaDetailsModal";
import PersonaReadyModal from "../Onboarding/PersonaReadyModal";
import HistoricalDatasetModal from "./HistoricalDatasetModal";
import UploadForecastModal from "./UploadForecastModal";
import ManageSchedulesModal from "./ManageSchedulesModal";
import CreateScheduleModal from "./CreateScheduleModal";
import GeneralDeleteModal from "./GeneralDeleteModal";
import EditScheduleModal from "./EditScheduleModal";
import HatUnlockModal from "./HatUnlockModal";
import ChecklistHintsModal from "./ChecklistHintsModal";
import ChecklistModal from "./ChecklistModal";
import ModelGardenModal from "./ModelGardenModal";
import PersonaNudgeModal from "../PersonaNudge/PersonaNudgeModal";
import ChatMapDrawerModal from "./ChatMapDrawerModal";
import ProcessedModelFilesModal from "./ProcessedModelFilesModal";
import ConfirmDeleteFileModal from "./ConfirmDeleteFileModal";
import AddProjectModal from "./AddProjectModal";
import ProjectTasksModal from "./ProjectTasksModal";
import ProjectImageUploadModal from "../imageUploader/imageUploadModals/ProjectImageUploadModal";
import ProfilePictureUploadModal from "../imageUploader/imageUploadModals/ProfilePictureUploadModal";
import OrganizationImageUploadModal from "../imageUploader/imageUploadModals/OrganizationImageUploadModal";
import ProjectChatModal from "./ProjectChatModal";
import ConfirmAddAssetFromMapModal from "./ConfirmAddAssetFromMapModal";
import ComposedForecastFormModal from "../ComposedForecast/ComposedForecastFormModal";
import ReportErrorModal from "../ErrorLog/ReportErrorModal";
import CreateOrganizationModal from "./CreateOrganizationModal";
import ManageOrganizationsModal from "./ManageOrganizationsModal";
import AccessCodesModal from "./AccessCodesModal";
import EnvVarsModal from "./EnvVarsModal";
import GraphPreviewerModal from "./GraphPreviewerModal";
import WelcomeTourModal from "./WelcomeTourModal";
import { MODAL_SIZE_WIDTHS, ModalSize, ModalFullScreenContext } from "./ModalBase";
import ShareLinkModal from "../share/ShareLinkModal";
import SharedLinksManager from "../share/SharedLinksManager";
import NoSubscriptionModal from "../AiChatComponent/panelComponents/NoSubscriptionModal";
import PlanLimitModal from "./PlanLimitModal";
import LightboxModal from "./LightboxModal";
// Lazy load larger components to improve performance
const LoggedInResetPassword = lazy(() => import("./LoggedInResetPassword"));
const SolarPowerForm = lazy(() => import("./SolarPowerForm"));
const EnergyCurveSelect = lazy(() => import("./EnergyCurveSelect"));
const ProcessModelDownload = lazy(() => import("./ProcessModelDownload"));
const ChangeProfilePicture = lazy(() => import("./ChangeProfilePicture"));
const QuickSetup = lazy(() => import("../QuickSetup/QuickSetup"));
const DatasetGraphModal = lazy(() => import("./DatasetGraphModal"));
const ComposedForecastDetailModal = lazy(() => import("../ComposedForecast/ComposedForecastDetailModal"));
const ComposedForecastCopyModal = lazy(() => import("../ComposedForecast/ComposedForecastCopyModal"));
const ModelOutputDetailModal = lazy(() => import("../ModelOutput/ModelOutputDetailModal"));
const HistoricalModelModal = lazy(() => import("./HistoricalModelModal"));
const EditDatasetModal = lazy(() => import("./EditDatasetModal"));
const ProcessedModelV1EmailScheduleModal = lazy(() => import("./ProcessedModelV1EmailScheduleModal"));
const AssignWatershedModal = lazy(() => import("./AssignWatershedModal"));

// Map modal types to components
const MODAL_COMPONENTS: Record<string, React.ComponentType<any>> = {
  [modalViewsConst.addAssetGroup]: NewAssetGroupModal,
  [modalViewsConst.editAssetGroup]: NewAssetGroupModal,
  [modalViewsConst.addAssetNEW]: NewAssetModal,
  [modalViewsConst.editAssetNEW]: NewAssetModal,
  [modalViewsConst.assetCallback]: NewAssetModal,
  [modalViewsConst.windCurveCallback]: PerformanceCurveForm,
  [modalViewsConst.addGroupFirst]: AddGroupFirst,
  [modalViewsConst.deleteAssetModal]: DeleteApprove,
  [modalViewsConst.deleteAssetGroup]: DeleteApprove,
  [modalViewsConst.deletePerformanceCurve]: DeleteApprove,
  [modalViewsConst.deleteSolarEnergyModel]: DeleteApprove,
  [modalViewsConst.deletePersona]: DeleteApprove,
  [modalViewsConst.addPerformanceCurve]: PerformanceCurveForm,
  [modalViewsConst.editPerformanceCurve]: PerformanceCurveForm,
  [modalViewsConst.powerCurveGraph]: PCurveDisplay,
  [modalViewsConst.solarEnergyModelPreview]: SolarModelEnergyDisplay,
  [modalViewsConst.assetPowerCurveEdit]: AssetPCurveEdit,
  [modalViewsConst.notification]: Notification,
  [modalViewsConst.platformConsent]: PlatformConsentModal,
  [modalViewsConst.assetImageUpload]: AssetImageUploadModal,
  [modalViewsConst.assetGroupImageUpload]: AssetGroupImageUploadModal,
  [modalViewsConst.newProcessedModelSchema]: DynamicProcessedModelSchemaModal,
  [modalViewsConst.customForecastEdit]: CustomForecastModal,
  [modalViewsConst.viewPersonaDetails]: PersonaDetailsModal,
  [modalViewsConst.newHistoricalDataset]: HistoricalDatasetModal,
  [modalViewsConst.uploadDatasetData]: HistoricalDatasetModal,
  [modalViewsConst.uploadForecast]: UploadForecastModal,
  [modalViewsConst.manageSchedules]: ManageSchedulesModal,
  [modalViewsConst.createSchedule]: CreateScheduleModal,
  [modalViewsConst.generalDelete]: GeneralDeleteModal,
  [modalViewsConst.editSchedule]: EditScheduleModal,
  [modalViewsConst.v1EmailSchedule]: ProcessedModelV1EmailScheduleModal,
  [modalViewsConst.watershedAssign]: AssignWatershedModal,
  [modalViewsConst.hatUnlock]: HatUnlockModal,
  [modalViewsConst.hatUnlockDone]: HatUnlockModal,
  [modalViewsConst.checklistHints]: ChecklistHintsModal,
  [modalViewsConst.checklist]: ChecklistModal,
  [modalViewsConst.modelGarden]: ModelGardenModal,
  [modalViewsConst.personaReady]: PersonaReadyModal,
  [modalViewsConst.personaNudge]: PersonaNudgeModal,
  [modalViewsConst.chatMapDraw]: ChatMapDrawerModal,
  [modalViewsConst.processedModelFiles]: ProcessedModelFilesModal,
  [modalViewsConst.confirmDeleteFile]: ConfirmDeleteFileModal,
  [modalViewsConst.addProject]: AddProjectModal,
  [modalViewsConst.viewProjectTasks]: ProjectTasksModal,
  [modalViewsConst.projectImageUpload]: ProjectImageUploadModal,
  [modalViewsConst.profileImageUpload]: ProfilePictureUploadModal,
  [modalViewsConst.organizationImageUpload]: OrganizationImageUploadModal,
  [modalViewsConst.projectChat]: ProjectChatModal,
  [modalViewsConst.datasetGraph]: DatasetGraphModal,
  [modalViewsConst.modelOutputDetail]: ModelOutputDetailModal,
  [modalViewsConst.historicalModel]: HistoricalModelModal,
  [modalViewsConst.editDataset]: EditDatasetModal,
  [modalViewsConst.confirmAddAssetFromMap]: ConfirmAddAssetFromMapModal,
  [modalViewsConst.provideFeedback]: ReportErrorModal,
  [modalViewsConst.adminCreateOrganization]: CreateOrganizationModal,
  [modalViewsConst.adminManageOrganizations]: ManageOrganizationsModal,
  [modalViewsConst.adminAccessCodes]: AccessCodesModal,
  [modalViewsConst.adminEnvVars]: EnvVarsModal,
  [modalViewsConst.adminGraphPreviewer]: GraphPreviewerModal,
  [modalViewsConst.manageSharedLinks]: SharedLinksManager,
  [modalViewsConst.noSubscription]: NoSubscriptionModal,
  [modalViewsConst.welcomeTour]: WelcomeTourModal,
  [modalViewsConst.assetLimitReached]: PlanLimitModal,
  [modalViewsConst.modelLimitReached]: PlanLimitModal,
  [modalViewsConst.createComposedForecast]: ComposedForecastFormModal,
  [modalViewsConst.editComposedForecast]: ComposedForecastFormModal,
  [modalViewsConst.deleteComposedForecast]: DeleteApprove,
  [modalViewsConst.composedForecastDetail]: ComposedForecastDetailModal,
  [modalViewsConst.copyComposedForecast]: ComposedForecastCopyModal,
  [modalViewsConst.shareEmbed]: ShareLinkModal,
  [modalViewsConst.lightbox]: LightboxModal,
  // Lazy loaded components
  [modalViewsConst.passwordChange]: LoggedInResetPassword,
  [modalViewsConst.newSolarPowerCurve]: SolarPowerForm,
  [modalViewsConst.energyModelSelect]: EnergyCurveSelect,
  [modalViewsConst.processedModelDownload]: ProcessModelDownload,
  [modalViewsConst.changeProfilePicture]: ChangeProfilePicture,
  [modalViewsConst.llmSetupAssets]: QuickSetup,
};

const ModalGlobal: React.FC = () => {
  const dispatch = useDispatch();
  const modalState = useSelector((state: RootState) => state.modalState);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Track analytics when modal opens TODO: Remove ReactGA and integrate PostHog for modal open tracking (usePostHogCapture).
  useEffect(() => {
    if (modalState.modalOpen && modalState.modalView) {
      ReactGA.event({
        category: "User",
        action: "Open Modal",
        label: modalState.modalView,
      });
    }
  }, [modalState.modalOpen, modalState.modalView]);
  
  // A modal can carry `__returnTo` to reopen another modal on close — forward the result opaquely so the target modal interprets it
  const reopenReturnTo = (result?: any): boolean => {
    const returnTo = modalState.properties?.__returnTo;
    if (!returnTo) return false;
    dispatch(setModalState({
      modalOpen: true,
      modalView: returnTo.modalView,
      properties: {
        ...returnTo.properties,
        ...(result ? { returnedResult: result } : {}),
      },
    }));
    return true;
  };

  // Close modal with result
  const handleClose = (result?: any) => {
    // Log the result for debugging (you can remove this in production)
    if (result && result.success) {
      console.log('Modal result:', result);
    }

    if (reopenReturnTo(result)) return;

    // Close the modal with the result
    dispatch(closeModalWithResult(result));
  };
  
  // Get the component to render
  const ModalComponent = modalState.modalView ? MODAL_COMPONENTS[modalState.modalView] : null;
  
  // Resolve the __modalSize token (if any) once for use across width + layout logic.
  const sizeToken = modalState?.properties?.__modalSize as ModalSize | undefined;
  const isModalBaseModal = !!(sizeToken && MODAL_SIZE_WIDTHS[sizeToken]);

  // Returns the intended pixel width of the current modal (ignoring viewport constraints). Used to decide whether the modal would touch the viewport edges.
  const getModalIntendedPixelWidth = (): number => {
    // "wideMap" is a legacy special-case value for __modalSize — not part of the ModalSize type.
    if (modalState?.properties?.__modalSize === "wideMap") return windowWidth * 0.95;

    if (isModalBaseModal) return parseInt(MODAL_SIZE_WIDTHS[sizeToken!]);

    if (modalState.modalView === modalViewsConst.editSchedule) return 720;
    if (modalState.modalView === modalViewsConst.checklist) return 480;
    if (modalState.modalView === modalViewsConst.hatUnlockDone) return 360;
    // if (modalState.modalView === modalViewsConst.personaNudge) return 420; // Hidden for now
    if (modalState.modalView === modalViewsConst.modelGarden) return 1200;
    return 710;
  };

  // Go full-screen when the modal would touch or nearly touch the viewport edges (< 32px margin).
  const isFullScreen = windowWidth < getModalIntendedPixelWidth() + 32;

  const getModalWidth = () => {
    if (isFullScreen) return "100%";
    // "wideMap" is a legacy special-case value for __modalSize — not part of the ModalSize type.
    if (modalState?.properties?.__modalSize === "wideMap") return "95vw";
    if (isModalBaseModal) return MODAL_SIZE_WIDTHS[sizeToken!];
    // addAssetForm uses a percentage width that doesn't map to a pixel value.
    if (modalState.modalView === modalViewsConst.addAssetForm) return "25%";
    return `${getModalIntendedPixelWidth()}px`;
  };


  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
  return (
    <Transition appear show={modalState.modalOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-[10050]"
        onClose={() => {
          // Don't allow backdrop/escape close for the consent modal, or the welcome tour modal — the latter must close via its own buttons so the welcomeShown flag and "Show me around" choice are always recorded before the chat tour can start.
          if (modalState.modalView === modalViewsConst.platformConsent) return;
          if (modalState.modalView === modalViewsConst.welcomeTour) return;
          // Esc/backdrop must also honor a __returnTo handoff (e.g. map-draw → project chat)
          if (reopenReturnTo()) return;
          dispatch(setModalOpen(false));
        }}
      >
        {/* Background overlay */}
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        {/* Modal container */}
        <div className="fixed inset-0 overflow-y-auto z-[10050]">
          <div className={`flex min-h-full ${isFullScreen ? 'items-start' : 'items-center'} justify-center`}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel 
                className={`w-full transform overflow-hidden ${isFullScreen ? 'rounded-none h-dvh' : 'rounded-lg'} bg-white shadow-xl transition-all`}
                style={{
                  maxWidth: getModalWidth(),
                  maxHeight: isFullScreen ? '100dvh' : '90dvh',
                  height: isFullScreen ? '100dvh' : 'auto',
                }}
              >
                <div className={`${isModalBaseModal ? 'overflow-hidden' : 'overflow-y-auto'} ${
                  isModalBaseModal ? '' :
                  isFullScreen ? 'h-dvh' :
                  (modalState.modalView === modalViewsConst.termsAndConditions || modalState.modalView === modalViewsConst.chatPrivacyPolicy) ? 'h-full' :
                  'max-h-[90dvh]'
                } ${
                  // Full screen dropped the gutter entirely, so legacy modals ran to the phone's edge; the header carries its own top padding, so only the sides and bottom are needed
                  isModalBaseModal ? '' : isFullScreen ? 'px-5 pb-5' : 'p-5'
                }`}>
                  <Suspense fallback={
                    /* Generic modal-shaped placeholder — a lazy chunk's own layout isn't known here */
                    <div role="status" aria-label="Loading" className="flex flex-col gap-4 p-6">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-8 w-8" radius="md" />
                        <Skeleton className="h-4 w-48" />
                      </div>
                      <SkeletonText lines={3} />
                      <Skeleton className="h-eai-control-lg w-full" radius="md" />
                      <Skeleton className="h-eai-control-lg w-full" radius="md" />
                      <div className="flex justify-end gap-2 pt-2">
                        <Skeleton className="h-eai-control-lg w-24" radius="md" />
                        <Skeleton className="h-eai-control-lg w-24" radius="md" />
                      </div>
                    </div>
                  }>
                    {ModalComponent && (
                      <ModalFullScreenContext.Provider value={isFullScreen}>
                      <ModalComponent
                        // Common props
                        onClose={handleClose}
                        
                        // Modal-specific props based on type
                        {...(modalState.modalView === modalViewsConst.editAssetGroup && {
                          editFlag: true,
                          initialAssetGroup: modalState.properties
                        })}
                        
                        {...(modalState.modalView === modalViewsConst.addAssetNEW && {
                          modalType: "newAsset",
                          initialAssetState: modalState.properties
                        })}
                        
                        {...(modalState.modalView === modalViewsConst.editAssetNEW && {
                          // editFlag: true,
                          modalType: "editAsset",
                          initialAssetState: modalState.properties
                        })}
                        {...(modalState.modalView === modalViewsConst.assetCallback && {
                          modalType: "callback",
                          initialAssetState: modalState.properties
                        })}
                        
                        {...(modalState.modalView === modalViewsConst.windCurveCallback && {
                          modalType: "callback",
                          initialPCurve: modalState.properties
                        })}
                        
                        {...(modalState.modalView === modalViewsConst.deleteAssetModal && {
                          data: modalState.properties,
                          deleteType: "asset",
                          deleteTypeLabel: "asset"
                        })}
                        
                        {...(modalState.modalView === modalViewsConst.deleteAssetGroup && {
                          data: modalState.properties,
                          deleteType: "assetGroup",
                          deleteTypeLabel: "asset Group"
                        })}
                        
                        {...(modalState.modalView === modalViewsConst.deletePerformanceCurve && {
                          data: modalState.properties,
                          deleteType: "pCurve",
                          deleteTypeLabel: "Wind Energy Model"
                        })}
                        
                        {...(modalState.modalView === modalViewsConst.deleteSolarEnergyModel && {
                          data: modalState.properties,
                          deleteType: "SolarEnergyModel",
                          deleteTypeLabel: "Solar Energy Model"
                        })}

                        {...(modalState.modalView === modalViewsConst.deletePersona && {
                          data: modalState.properties,
                          deleteType: "persona",
                          deleteTypeLabel: "Profile"
                        })}
                        
                        {...(modalState.modalView === modalViewsConst.editPerformanceCurve && {
                          editFlag: true,
                          initialPCurve: modalState.properties
                        })}

                        {...(modalState.modalView === modalViewsConst.newSolarPowerCurve && modalState.properties && {
                          editFlag: true,
                          initialSPState: modalState.properties
                        })}

                        {...(modalState.modalView === modalViewsConst.powerCurveGraph && {
                          graphProperties: modalState.properties
                        })}
                        
                        {...(modalState.modalView === modalViewsConst.solarEnergyModelPreview && {
                          modelProperties: modalState.properties
                        })}
                        
                        {...(modalState.modalView === modalViewsConst.assetPowerCurveEdit && {
                          asset: modalState.properties
                        })}
                        
                        {...(modalState.modalView === modalViewsConst.notification && {
                          header: modalState?.properties?.header,
                          details: modalState?.properties?.details,
                          url: modalState?.properties?.url,
                          loading: modalState?.properties?.loading
                        })}
                        
                        {...(modalState.modalView === modalViewsConst.processedModelDownload && {
                          asset: modalState.properties.asset,
                          assetOwner: modalState.properties.assetOwner,
                          initialRouteShort: modalState.properties.initialRouteShort
                        })}
                        
                        {...(modalState.modalView === modalViewsConst.platformConsent && {
                          userId: modalState.properties?.userId
                        })}

                        {...(modalState.modalView === modalViewsConst.assetImageUpload && {
                          asset: modalState.properties
                        })}

                        {...(modalState.modalView === modalViewsConst.assetGroupImageUpload && {
                          asset: modalState.properties
                        })}

                        {...(modalState.modalView === modalViewsConst.organizationImageUpload && {
                          asset: modalState.properties
                        })}

                        {...(modalState.modalView === modalViewsConst.projectImageUpload && {
                          asset: modalState.properties
                        })}

                        {...(modalState.modalView === modalViewsConst.addProject && {
                          initialTitle: modalState.properties?.initialTitle,
                          initialDescription: modalState.properties?.initialDescription,
                          initialImageUrl: modalState.properties?.initialImageUrl,
                          returnedResult: modalState.properties?.returnedResult,
                        })}

                        {...(modalState.modalView === modalViewsConst.customForecastEdit && {
                          asset: modalState.properties
                        })}

                        {...(modalState.modalView === modalViewsConst.viewPersonaDetails && {
                          persona: modalState.properties
                        })}

                        {...(modalState.modalView === modalViewsConst.personaReady && {
                          summary: modalState.properties
                        })}
                        {...(modalState.modalView === modalViewsConst.generalDelete && {
                          title: modalState.properties?.title,
                          itemName: modalState.properties?.itemName,
                          itemId: modalState.properties?.itemId,
                          itemType: modalState.properties?.itemType,
                          cancelView: modalState.properties?.cancelView,
                          cancelProperties: modalState.properties?.cancelProperties,
                          confirmView: modalState.properties?.confirmView,
                          confirmProperties: modalState.properties?.confirmProperties,
                        })}

                        {...(modalState.modalView === modalViewsConst.createSchedule && {
                          projectId: modalState.properties?.projectId,
                        })}

                        {...(modalState.modalView === modalViewsConst.editSchedule && {
                          schedule: modalState.properties?.schedule,
                        })}

                        {...(modalState.modalView === modalViewsConst.v1EmailSchedule && {
                          schedule: modalState.properties?.schedule,
                          variant: modalState.properties?.variant,
                        })}

                        {...(modalState.modalView === modalViewsConst.watershedAssign && {
                          watershed: modalState.properties?.watershed,
                        })}

                        {...(modalState.modalView === modalViewsConst.processedModelFiles && {
                          model: modalState.properties?.model
                        })}

                        {...(modalState.modalView === modalViewsConst.confirmDeleteFile && {
                          filePath: modalState.properties?.filePath,
                          fileName: modalState.properties?.fileName,
                          model: modalState.properties?.model,
                        })}

                        {...(modalState.modalView === modalViewsConst.confirmAddAssetFromMap && {
                          lat: modalState.properties?.lat,
                          lng: modalState.properties?.lng,
                          assetGroupId: modalState.properties?.assetGroupId,
                        })}

                        {...(modalState.modalView === modalViewsConst.provideFeedback && {
                          conversationId: modalState.properties?.conversationId,
                          userEmail: modalState.properties?.userEmail,
                        })}

                        {...((modalState.modalView === modalViewsConst.assetLimitReached ||
                              modalState.modalView === modalViewsConst.modelLimitReached) && {
                          itemLabel: modalState.modalView === modalViewsConst.modelLimitReached ? "model" : "asset",
                          tier: modalState.properties?.tier,
                          limit: modalState.properties?.limit,
                          currentCount: modalState.properties?.currentCount,
                          source: modalState.properties?.source,
                        })}

                        {...((modalState.modalView === modalViewsConst.createComposedForecast ||
                              modalState.modalView === modalViewsConst.editComposedForecast) && {
                          mode: modalState.modalView === modalViewsConst.createComposedForecast ? "create" : "edit",
                          forecast: modalState.properties?.forecast,
                        })}

                        {...(modalState.modalView === modalViewsConst.deleteComposedForecast && {
                          data: modalState.properties,
                          deleteType: "composedForecast",
                          deleteTypeLabel: "Custom View",
                        })}

                        {...((modalState.modalView === modalViewsConst.composedForecastDetail ||
                              modalState.modalView === modalViewsConst.copyComposedForecast) && {
                          forecast: modalState.properties?.forecast,
                        })}

                        {...(modalState.modalView === modalViewsConst.projectChat && {
                          projectId: modalState.properties?.projectId,
                          projectTitle: modalState.properties?.projectTitle,
                          initialConversationId: modalState.properties?.initialConversationId,
                          initialInputValue: modalState.properties?.initialInputValue,
                          projectTaskId: modalState.properties?.projectTaskId,
                          initialAttachedFiles: modalState.properties?.initialAttachedFiles,
                          returnedResult: modalState.properties?.returnedResult,
                        })}
                        {...(modalState.modalView === modalViewsConst.datasetGraph && {
                          dataset: modalState.properties?.dataset,
                          initialTab: modalState.properties?.initialTab,
                        })}
                        {...(modalState.modalView === modalViewsConst.historicalModel && {
                          model: modalState.properties?.model,
                          initialTab: modalState.properties?.initialTab,
                        })}
                        {...(modalState.modalView === modalViewsConst.modelOutputDetail && {
                          schema: modalState.properties?.schema,
                          initialTab: modalState.properties?.initialTab,
                        })}
                        {...(modalState.modalView === modalViewsConst.editDataset && {
                          dataset: modalState.properties?.dataset,
                          focusMapping: modalState.properties?.focusMapping,
                          source: modalState.properties?.source,
                          canDelete: modalState.properties?.canDelete,
                        })}
                        {...(modalState.modalView === modalViewsConst.lightbox && {
                          items: modalState.properties?.items,
                          initialIndex: modalState.properties?.initialIndex,
                        })}
                        {...(modalState.modalView === modalViewsConst.shareEmbed && {
                          runnerId: modalState.properties?.runnerId,
                          conversationId: modalState.properties?.conversationId,
                          messageId: modalState.properties?.messageId,
                        })}
                      />
                      </ModalFullScreenContext.Provider>
                    )}
                  </Suspense>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ModalGlobal;
